import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Grid2, TextField } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useFormStyles } from "../../../hooks/useFormStyles";
import { ContactForm as ContactFormType, getContactFormSchema } from "../../../types/forms/entity";
import { EntityContact } from "../../../types/model/entity/EntityContact";

interface ContactFormProps {
  contactData?: EntityContact;
  formId: string;
  isEditing: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  onSubmit: (values: ContactFormType) => Promise<void>;
}

export default function ContactForm({
  contactData,
  formId,
  isEditing,
  isSaving,
  isDeleting,
  onSubmit,
}: Readonly<ContactFormProps>) {
  const { t } = useTranslation();
  const formStyles = useFormStyles(isEditing);

  const validationSchema = getContactFormSchema(t);
  const methods = useForm<ContactFormType>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      email: contactData?.email || "",
      mobile: contactData?.mobile || "",
      phone: contactData?.phone || "",
    },
  });

  const handleFormSubmit = async (values: ContactFormType) => {
    await onSubmit(values);
  };

  return (
    <FormProvider {...methods}>
      <Box component="form" id={formId} onSubmit={methods.handleSubmit(handleFormSubmit)}>
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12, sm: 12 }}>
            <TextField
              fullWidth
              label={t("entities.entity.contacts.email")}
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing || isSaving || isDeleting}
              sx={{ ...formStyles }}
              {...methods.register("email")}
              error={!!methods.formState.errors.email}
              helperText={methods.formState.errors.email?.message as string | undefined}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label={t("entities.entity.contacts.mobile")}
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing || isSaving || isDeleting}
              sx={{ ...formStyles }}
              {...methods.register("mobile")}
              error={!!methods.formState.errors.mobile}
              helperText={methods.formState.errors.mobile?.message as string | undefined}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label={t("entities.entity.contacts.phone")}
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing || isSaving || isDeleting}
              sx={{ ...formStyles }}
              {...methods.register("phone")}
              error={!!methods.formState.errors.phone}
              helperText={methods.formState.errors.phone?.message as string | undefined}
            />
          </Grid2>
        </Grid2>
      </Box>
    </FormProvider>
  );
}
